import { validArticleDates } from "./articleDates";
import type {
  AbridgedDeliveryStatus,
  Annotation,
  AnnotationEnvelope,
  Briefing,
  BriefingEnvelope,
  DailyContext,
  DailyEditionEnvelope,
  DeferredCaptureMaterialization,
  FeedSource,
  FollowingEnvelope,
  InboxBrief,
  InboxBriefEnvelope,
  InboxBriefPreferences,
  ItemListEnvelope,
  OfflineBundleEnvelope,
  OfflineManifestEnvelope,
  PodcastEpisode,
  PodcastEpisodeEnvelope,
  PodcastPreferencesEnvelope,
  ReaderItem,
  SaveItemEnvelope,
  ReadingSessionInput,
  SearchItemListEnvelope,
} from "./types";
import { hasArray, hasBoolean, hasFiniteNumber, hasNullableString, hasString, isJsonObject, objectField, strictJsonEnvelope, withRequestTimeout, type JsonObject } from "./json";
import { validReadingCopy } from "./readingCopy";

export class ApiError extends Error {
  constructor(public status: number, public code: string) {
    super(code);
  }
}

export interface ClientToken {
  id: string;
  label: string;
  scopes: string[];
  created_at: string;
  last_used_at: string;
  revoked_at: string;
  expires_at: string;
}

export interface CreatedClientToken extends ClientToken {
  token: string;
}

export function isUnauthorizedApiError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401;
}

async function requestJson<T>(path: string, init: RequestInit | undefined, validate: (payload: JsonObject, response: Response) => boolean): Promise<T> {
  return withRequestTimeout(init?.signal, async (signal) => {
    const response = await fetch(path, { ...init, credentials: "same-origin", signal });
    const payload = await strictJsonEnvelope(response, (status, code) => new ApiError(status, code), "request_failed");
    if (!validate(payload, response)) throw new ApiError(response.status, "invalid_response");
    return payload as T;
  });
}

const validOk = () => true;
const validStatus = (payload: JsonObject) => hasString(payload, "status");
const MAX_LIST_ITEMS = 50;
const MAX_SEARCH_ITEMS = 25;
const MAX_ITEM_SUMMARY_IDS = 32;
const MAX_OFFLINE_MANIFEST_ITEMS = 50;
const MAX_OFFLINE_BUNDLE_ITEMS = 8;
const MAX_OFFLINE_BUNDLE_BYTES = 4 * 1024 * 1024;
const MAX_BRIEFINGS = 25;
const MAX_ANNOTATIONS = 100;
const MAX_FOLLOWING_SOURCES = 200;
const MAX_INBOX_BRIEFS = 25;
const MAX_PODCAST_EPISODES = 25;
const MAX_DAILY_ITEMS = 256;
const MAX_DAILY_TITLE_CHARS = 48;
const MAX_DAILY_SUBTITLE_CHARS = 96;
const MAX_DAILY_SECTION_LABEL_CHARS = 24;
const MAX_DAILY_BRIEF_CHARS = 160;
const MAX_BRIEFING_ENTRIES = 12;
const MAX_BRIEFING_ACTIONS = 512;
const SEARCH_SCAN_LIMIT = 2_000;
const MAX_SEARCH_QUERY_CHARS = 512;
const MAX_PAGINATION_CURSOR_CHARS = 2_048;
const PAGINATION_CURSOR_PATTERN = /^[A-Za-z0-9_-]+$/;
const SEARCH_CURSOR_PATTERN = /^rsc_[a-f0-9]{32}$/;
const SEARCH_TRUNCATION_REASONS = new Set<string>(["snapshot_limit", "byte_budget", "document_limit", "tag_limit", "artifact_invalid"]);
const SEARCH_RESOURCE_TRUNCATION_REASONS = new Set<string>(["byte_budget", "document_limit", "tag_limit", "artifact_invalid"]);
const DAILY_FOCUS_LEVELS = new Set<string>(["low", "medium", "high"]);
const DEFERRED_MATERIALIZATION_REASONS = new Set<string>([
  "capture_assignment_recovery_required",
  "capture_identity_adoption_required",
  "capture_source_lane_schema_pending",
  "capture_intake_materialization_pending",
  "source_identity_runtime_paused",
  "source_identity_runtime_staged",
]);

function nonBlankString(value: unknown): value is string {
  return typeof value === "string" && Boolean(value.trim());
}

function boundedNonBlankString(value: unknown, maxLength: number): value is string {
  return nonBlankString(value) && value.length <= maxLength;
}

function normalizeSearchQuery(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function validPagination(payload: JsonObject): boolean {
  const hasMore = payload.has_more;
  const nextCursor = payload.next_cursor;
  if (typeof hasMore !== "boolean") return false;
  if (!hasMore) return nextCursor === null;
  return typeof nextCursor === "string"
    && nextCursor.length <= MAX_PAGINATION_CURSOR_CHARS
    && PAGINATION_CURSOR_PATTERN.test(nextCursor);
}

function validUniqueArray<T>(
  value: unknown,
  validate: (entry: unknown) => entry is T,
  idFor: (entry: T) => string,
  maxLength: number,
): value is T[] {
  if (!Array.isArray(value) || value.length > maxLength || !value.every(validate)) return false;
  const ids = value.map(idFor);
  return ids.every(nonBlankString) && new Set(ids).size === ids.length;
}

export function validReaderItem(value: unknown): value is ReaderItem {
  return isJsonObject(value)
    && validCanonicalItemId(value.id)
    && nonBlankString(value.title)
    && hasString(value, "url")
    && nonBlankString(value.status)
    && (value.reading_copy === undefined || value.reading_copy === null || validReadingCopy(value.reading_copy))
    && (value.article_dates === undefined || validArticleDates(value.article_dates))
    && (value.library_placement === undefined || value.library_placement === null || validLibraryPlacement(value.library_placement))
    && (value.reading_progress === undefined || (typeof value.reading_progress === "number" && Number.isFinite(value.reading_progress) && value.reading_progress >= 0 && value.reading_progress <= 1));
}

function validOfflineManifest(payload: JsonObject): boolean {
  return payload.schema_version === "imprint.offline-manifest.v1"
    && typeof payload.generated_at === "string"
    && Number.isFinite(Date.parse(payload.generated_at))
    && validUniqueArray(payload.items, (value): value is JsonObject => isJsonObject(value)
      && validReaderItem(value)
      && typeof value.content_version === "string"
      && value.content_version.length <= 128
      && Number.isSafeInteger(value.markdown_byte_count)
      && (value.markdown_byte_count as number) >= 0
      && (value.markdown_byte_count as number) <= 8 * 1024 * 1024,
    (value) => value.id as string, MAX_OFFLINE_MANIFEST_ITEMS)
    && validPagination(payload);
}

function validOfflineBundle(payload: JsonObject, requested: ReadonlyMap<string, string>): boolean {
  if (payload.schema_version !== "imprint.offline-bundle.v1"
    || !validUniqueArray(payload.items, (value): value is JsonObject => isJsonObject(value)
      && validReaderItem(value)
      && typeof value.markdown === "string"
      && Boolean(value.markdown)
      && requested.get(String(value.id)) === value.content_version
      && Number.isSafeInteger(value.markdown_byte_count)
      && (value.markdown_byte_count as number) > 0,
    (value) => value.id as string, MAX_OFFLINE_BUNDLE_ITEMS)
    || !Array.isArray(payload.unavailable)
    || payload.unavailable.length > MAX_OFFLINE_BUNDLE_ITEMS
    || !Number.isSafeInteger(payload.byte_count)
    || (payload.byte_count as number) < 0
    || (payload.byte_count as number) > MAX_OFFLINE_BUNDLE_BYTES) return false;
  const returned = new Set((payload.items as JsonObject[]).map((item) => String(item.id)));
  const unavailable = new Set<string>();
  const codes = new Set(["item_not_found", "content_version_changed", "markdown_unavailable", "bundle_byte_limit"]);
  for (const value of payload.unavailable) {
    if (!isJsonObject(value)
      || !requested.has(String(value.id))
      || returned.has(String(value.id))
      || unavailable.has(String(value.id))
      || !codes.has(String(value.code))) return false;
    unavailable.add(String(value.id));
  }
  return returned.size + unavailable.size === requested.size;
}

function validLibraryPlacement(value: unknown): boolean {
  return isJsonObject(value)
    && (value.kind === "continue" || value.kind === "lift")
    && nonBlankString(value.label)
    && (value.reason === undefined || typeof value.reason === "string")
    && (value.assignment_date === undefined || typeof value.assignment_date === "string");
}

function validReaderItemEnvelope(payload: JsonObject, expectedId?: string): boolean {
  return validReaderItem(payload.item) && (expectedId === undefined || payload.item.id === expectedId);
}

function validDeferredCaptureMaterialization(value: unknown): value is DeferredCaptureMaterialization {
  return isJsonObject(value)
    && value.object === "capture_materialization"
    && typeof value.capture_id === "string"
    && /^cap_[A-Za-z0-9]{1,160}$/.test(value.capture_id)
    && value.materialization_status === "deferred"
    && typeof value.deferred_reason === "string"
    && DEFERRED_MATERIALIZATION_REASONS.has(value.deferred_reason);
}

function validCapturePointer(value: unknown): value is JsonObject {
  return isJsonObject(value)
    && typeof value.id === "string"
    && /^cap_[A-Za-z0-9]{1,160}$/.test(value.id);
}

function validSaveItemEnvelope(payload: JsonObject, response: Response): boolean {
  if (payload.acquisition !== undefined && (!isJsonObject(payload.acquisition)
    || !["available", "source_blocked", "unavailable"].includes(String(payload.acquisition.state)))) return false;
  if (response.status === 202) {
    return payload.ok === true
      && payload.item === null
      && validCapturePointer(payload.capture)
      && validDeferredCaptureMaterialization(payload.materialization)
      && payload.capture.id === payload.materialization.capture_id;
  }
  return response.status === 201
    && payload.ok === true
    && validReaderItem(payload.item)
    && !String(payload.item.id).startsWith("cir_")
    && (payload.capture === undefined || validCapturePointer(payload.capture));
}

export function validItemList(payload: JsonObject, maxItems = MAX_LIST_ITEMS, allowedIds?: ReadonlySet<string>): boolean {
  if (!validUniqueArray(payload.items, validReaderItem, (item) => item.id, maxItems)) return false;
  if (allowedIds && payload.items.some((item) => !allowedIds.has(item.id))) return false;
  if (payload.results !== undefined) {
    if (!validUniqueArray(payload.results, validReaderItem, (item) => item.id, maxItems)) return false;
    const itemIds = payload.items.map((item) => item.id);
    const resultIds = payload.results.map((item) => item.id);
    if (itemIds.length !== resultIds.length || itemIds.some((id, index) => resultIds[index] !== id)) return false;
  }
  return hasArray(payload, "items") && validPagination(payload);
}

export function validSearchItemList(
  payload: JsonObject,
  maxItems: number,
  expectedQuery: string,
  minimumScannedCount = 0,
): boolean {
  const scannedCount = payload.search_scanned_count;
  const truncationReason = payload.search_truncation_reason;
  const truncationReasons = payload.search_truncation_reasons;
  if (!validItemList(payload, maxItems)
    || !hasString(payload, "query")
    || payload.query !== normalizeSearchQuery(expectedQuery)
    || !hasBoolean(payload, "search_complete")
    || !hasBoolean(payload, "search_truncated")
    || typeof truncationReason !== "string"
    || !Array.isArray(truncationReasons)
    || truncationReasons.length > SEARCH_TRUNCATION_REASONS.size
    || truncationReasons.some((reason) => typeof reason !== "string" || !SEARCH_TRUNCATION_REASONS.has(reason))
    || new Set(truncationReasons).size !== truncationReasons.length
    || truncationReason !== (truncationReasons[0] || "")
    || !validCount(scannedCount)
    || !validCount(minimumScannedCount)
    || scannedCount < minimumScannedCount
    || scannedCount > SEARCH_SCAN_LIMIT
    || (payload.search_pause_reason !== undefined && (
      typeof payload.search_pause_reason !== "string"
      || !["", "time_budget", "read_timeout"].includes(payload.search_pause_reason)
      || (payload.search_pause_reason !== "" && payload.search_status !== "in_progress")
    ))
    || payload.search_scan_limit !== SEARCH_SCAN_LIMIT) return false;
  if (payload.search_status === "in_progress") {
    return payload.search_complete === false
      && payload.search_truncated === false
      && truncationReasons.length === 0
      && scannedCount <= SEARCH_SCAN_LIMIT
      && payload.has_more === true
      && typeof payload.next_cursor === "string"
      && SEARCH_CURSOR_PATTERN.test(payload.next_cursor);
  }
  if (payload.search_status === "complete") {
    return payload.search_complete === true
      && payload.search_truncated === false
      && truncationReasons.length === 0
      && payload.has_more === false
      && payload.next_cursor === null;
  }
  if (payload.search_status === "truncated") {
    const snapshotAtLimit = truncationReasons.length === 1
      && truncationReasons[0] === "snapshot_limit"
      && scannedCount === SEARCH_SCAN_LIMIT;
    const resourceBelowLimit = truncationReasons.length === 1
      && SEARCH_RESOURCE_TRUNCATION_REASONS.has(truncationReasons[0])
      && scannedCount < SEARCH_SCAN_LIMIT;
    const snapshotThenResourceBelowLimit = truncationReasons.length === 2
      && truncationReasons[0] === "snapshot_limit"
      && SEARCH_RESOURCE_TRUNCATION_REASONS.has(truncationReasons[1])
      && scannedCount < SEARCH_SCAN_LIMIT;
    return payload.search_complete === false
      && payload.search_truncated === true
      && (snapshotAtLimit || resourceBelowLimit || snapshotThenResourceBelowLimit)
      && payload.has_more === false
      && payload.next_cursor === null;
  }
  return false;
}

export function validBriefing(value: unknown): value is Briefing {
  if (!(isJsonObject(value)
    && nonBlankString(value.id)
    && nonBlankString(value.title)
    && hasString(value, "subtitle")
    && typeof value.format_version === "string"
    && BRIEFING_FORMATS.has(value.format_version)
    && value.state === "ready"
    && nonBlankString(value.issue_date)
    && validCount(value.entry_count)
    && validCount(value.covered_count)
    && validCount(value.read_full_count)
    && validCount(value.duration_seconds)
    && hasString(value, "visual_markdown")
    && validUniqueArray(value.entries, (entry): entry is JsonObject => isJsonObject(entry)
      && validCanonicalItemId(entry.item_id)
      && typeof entry.treatment === "string"
      && BRIEFING_TREATMENTS.has(entry.treatment)
      && hasString(entry, "title")
      && hasString(entry, "orientation")
      && hasString(entry, "why_read")
      && hasString(entry, "visual_markdown")
      && typeof entry.state === "string"
      && BRIEFING_ENTRY_STATES.has(entry.state), (entry) => entry.item_id as string, MAX_BRIEFING_ENTRIES)
    && Array.isArray(value.actions)
    && value.actions.length <= MAX_BRIEFING_ACTIONS
    && value.actions.every((action) => isJsonObject(action)
      && validCanonicalItemId(action.item_id)
      && typeof action.action === "string"
      && BRIEFING_ACTIONS.has(action.action)))) return false;
  if (value.covered_count + value.read_full_count !== value.entry_count) return false;
  const entries = value.entries as JsonObject[];
  if (value.format_version === "abridged_v1"
    && (value.read_full_count !== 0 || entries.some((entry) => entry.treatment !== "covered"))) return false;
  return entries.length === value.entry_count
    && entries.filter((entry) => entry.treatment === "covered").length === value.covered_count
    && entries.filter((entry) => entry.treatment === "read_full").length === value.read_full_count;
}

const BRIEFING_FORMATS = new Set(["abridged_v1", "briefing_v1", "legacy_podcast_v1"]);
const BRIEFING_TREATMENTS = new Set(["covered", "read_full"]);
const BRIEFING_ENTRY_STATES = new Set(["pending", "kept", "archived", "restored", "skipped_changed", "not_eligible"]);
const BRIEFING_ACTIONS = new Set(["kept", "archived", "skipped_changed", "restored"]);

function validCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function validBriefingSummary(value: unknown): boolean {
  return isJsonObject(value)
    && nonBlankString(value.id)
    && nonBlankString(value.title)
    && hasString(value, "subtitle")
    && nonBlankString(value.issue_date)
    && typeof value.format_version === "string"
    && BRIEFING_FORMATS.has(value.format_version)
    && value.state === "ready"
    && validCount(value.entry_count)
    && validCount(value.covered_count)
    && validCount(value.read_full_count)
    && value.covered_count + value.read_full_count === value.entry_count;
}

export function validBriefingEnvelope(payload: JsonObject): boolean {
  return validUniqueArray(payload.briefings, (value): value is JsonObject => isJsonObject(value) && validBriefingSummary(value), (value) => value.id as string, MAX_BRIEFINGS)
    && validPagination(payload);
}

function validBriefingField(payload: JsonObject, nullable = false, expectedId?: string): boolean {
  return nullable && payload.briefing === null
    ? true
    : validBriefing(payload.briefing) && (expectedId === undefined || payload.briefing.id === expectedId);
}

const ABRIDGED_DELIVERY_STATES = new Set(["pending", "generating", "recovering", "delayed", "failed", "no_suitable_items", "ready"]);
const ABRIDGED_SWEEP_KINDS = new Set(["", "scheduled", "recovery"]);
const ABRIDGED_SWEEP_DECISIONS = new Set(["", "requested", "requested_missing", "requeued", "ready", "in_progress", "not_eligible", "attempt_cap", "error"]);

export function validAbridgedDelivery(value: unknown): value is AbridgedDeliveryStatus {
  return isJsonObject(value)
    && /^\d{4}-\d{2}-\d{2}$/.test(String(value.issue_date || ""))
    && ABRIDGED_DELIVERY_STATES.has(String(value.state || ""))
    && typeof value.error_code === "string"
    && (value.error_code === "" || /^[A-Za-z0-9_.:-]{1,100}$/.test(value.error_code))
    && validCount(value.attempts)
    && typeof value.next_recovery_at === "string"
    && (value.next_recovery_at === "" || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value.next_recovery_at))
    && boundedNonBlankString(value.timezone, 64)
    && /^[A-Za-z0-9_+.-]+(?:\/[A-Za-z0-9_+.-]+)*$/.test(value.timezone)
    && ABRIDGED_SWEEP_KINDS.has(String(value.last_sweep_kind ?? ""))
    && ABRIDGED_SWEEP_DECISIONS.has(String(value.last_sweep_decision ?? ""))
    && typeof value.last_sweep_at === "string"
    && value.last_sweep_at.length <= 40;
}

export function validDaily(payload: JsonObject): boolean {
  return nonBlankString(payload.issue_date)
    && nonBlankString(payload.context_bucket)
    && (payload.issue === null || validDailyIssue(payload.issue))
    && validUniqueArray(payload.entries, validDailyEntry, (entry) => entry.item_id as string, MAX_DAILY_ITEMS);
}

function validDailyForContext(payload: JsonObject, context: DailyContext): boolean {
  return validDaily(payload)
    && payload.issue_date === context.issueDate
    && payload.context_bucket === context.bucket;
}

function validDailyIssue(value: unknown): boolean {
  if (!isJsonObject(value)
    || !boundedNonBlankString(value.title, MAX_DAILY_TITLE_CHARS)
    || !boundedNonBlankString(value.subtitle, MAX_DAILY_SUBTITLE_CHARS)) return false;
  const validIssueItem = (item: unknown): item is JsonObject => isJsonObject(item)
    && validCanonicalItemId(item.item_id)
    && (item.rank === undefined || (typeof item.rank === "number" && Number.isInteger(item.rank) && item.rank >= 1 && item.rank <= 32))
    && (item.section === undefined || boundedNonBlankString(item.section, MAX_DAILY_SECTION_LABEL_CHARS));
  let referenceCount = 0;
  if (value.items !== undefined) {
    if (!validUniqueArray(value.items, validIssueItem, (item) => item.item_id as string, MAX_DAILY_ITEMS)) return false;
    referenceCount += value.items.length;
  }
  if (value.sections === undefined) return referenceCount > 0;
  if (!Array.isArray(value.sections) || value.sections.length > MAX_DAILY_ITEMS) return false;
  for (const section of value.sections) {
    if (!isJsonObject(section) || !boundedNonBlankString(section.label, MAX_DAILY_SECTION_LABEL_CHARS)) return false;
    let sectionReferences = 0;
    if (section.items !== undefined) {
      if (!validUniqueArray(section.items, validIssueItem, (item) => item.item_id as string, MAX_DAILY_ITEMS)) return false;
      sectionReferences += section.items.length;
    }
    if (section.item_ids !== undefined) {
      if (!Array.isArray(section.item_ids)
        || section.item_ids.length > MAX_DAILY_ITEMS
        || !section.item_ids.every(validCanonicalItemId)
        || new Set(section.item_ids).size !== section.item_ids.length) return false;
      sectionReferences += section.item_ids.length;
    }
    if (sectionReferences === 0) return false;
    referenceCount += sectionReferences;
    if (referenceCount > MAX_DAILY_ITEMS) return false;
  }
  return referenceCount > 0;
}

function validDailyEntry(value: unknown): value is JsonObject {
  return isJsonObject(value)
    && validCanonicalItemId(value.item_id)
    && (value.one_line_brief === undefined || boundedNonBlankString(value.one_line_brief, MAX_DAILY_BRIEF_CHARS))
    && (value.why_read_now === undefined || boundedNonBlankString(value.why_read_now, MAX_DAILY_BRIEF_CHARS))
    && (value.category_label === undefined || boundedNonBlankString(value.category_label, MAX_DAILY_SECTION_LABEL_CHARS))
    && (value.focus_level === undefined || (typeof value.focus_level === "string" && DAILY_FOCUS_LEVELS.has(value.focus_level)))
    && (value.selected === undefined || typeof value.selected === "boolean")
    && (value.rank === undefined || (typeof value.rank === "number" && Number.isInteger(value.rank) && value.rank >= 1 && value.rank <= 32))
    && (value.section === undefined || boundedNonBlankString(value.section, MAX_DAILY_SECTION_LABEL_CHARS));
}

export function validAnnotation(value: unknown): value is Annotation {
  return isJsonObject(value) && nonBlankString(value.id) && validCanonicalItemId(value.item_id) && hasString(value, "text");
}

export function validAnnotationEnvelope(payload: JsonObject): boolean {
  return validUniqueArray(payload.annotations, validAnnotation, (annotation) => annotation.id, MAX_ANNOTATIONS)
    && hasBoolean(payload, "has_more")
    && hasNullableString(payload, "next_cursor");
}

function validFeedSource(value: unknown): value is FeedSource {
  return isJsonObject(value)
    && nonBlankString(value.id)
    && hasString(value, "title")
    && hasString(value, "feed_url_host")
    && hasString(value, "feed_url_redacted")
    && hasString(value, "feed_type")
    && hasBoolean(value, "enabled");
}

function validFollowing(payload: JsonObject): boolean {
  return validUniqueArray(payload.feed_sources, validFeedSource, (source) => source.id, MAX_FOLLOWING_SOURCES);
}

export function validInboxBrief(value: unknown): value is InboxBrief {
  return isJsonObject(value) && nonBlankString(value.id) && nonBlankString(value.title) && hasString(value, "subtitle") && nonBlankString(value.status);
}

export function validInboxBriefEnvelope(payload: JsonObject): boolean {
  return validUniqueArray(payload.briefs, validInboxBrief, (brief) => brief.id, MAX_INBOX_BRIEFS);
}

export function validPodcastEpisode(value: unknown): value is PodcastEpisode {
  return isJsonObject(value)
    && nonBlankString(value.id)
    && nonBlankString(value.title)
    && hasString(value, "subtitle")
    && nonBlankString(value.status)
    && nonBlankString(value.issue_date)
    && nonBlankString(value.created_at)
    && validUniqueArray(value.variants, (variant): variant is JsonObject => isJsonObject(variant) && nonBlankString(variant.id) && nonBlankString(variant.voice), (variant) => variant.id as string, 8)
    && (value.sources === undefined || validUniqueArray(value.sources, (source): source is JsonObject => isJsonObject(source) && validCanonicalItemId(source.item_id) && nonBlankString(source.state), (source) => source.item_id as string, MAX_DAILY_ITEMS));
}

export function validPodcastEpisodeEnvelope(payload: JsonObject): boolean {
  return validUniqueArray(payload.episodes, validPodcastEpisode, (episode) => episode.id, MAX_PODCAST_EPISODES);
}

function validInboxPreferences(payload: JsonObject): boolean {
  const preferences = objectField(payload, "preferences");
  return Boolean(preferences && hasBoolean(preferences, "enabled") && hasString(preferences, "updated_at"));
}

function validPodcastPreferences(payload: JsonObject): boolean {
  const preferences = objectField(payload, "preferences");
  return Boolean(preferences
    && hasBoolean(payload, "can_manage_feed")
    && hasBoolean(preferences, "enabled")
    && hasString(preferences, "updated_at")
    && (payload.feed_url === undefined || typeof payload.feed_url === "string"));
}

function validClientToken(value: unknown, reveal = false): value is CreatedClientToken | ClientToken {
  if (!isJsonObject(value)
    || !hasString(value, "id")
    || !hasString(value, "label")
    || !hasArray(value, "scopes")
    || !(value.scopes as unknown[]).every((scope) => typeof scope === "string")
    || !hasString(value, "created_at")
    || !hasString(value, "last_used_at")
    || !hasString(value, "revoked_at")
    || !hasString(value, "expires_at")) return false;
  return !reveal || hasString(value, "token");
}

function jsonInit(method: "POST" | "PATCH" | "PUT", body: object): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

export const clientTokenApi = {
  list() {
    return requestJson<{ ok: boolean; tokens: ClientToken[] }>(
      "/auth/tokens",
      undefined,
      (payload) => Array.isArray(payload.tokens)
        && payload.tokens.length <= 100
        && payload.tokens.every((token) => validClientToken(token)),
    ).then((value) => value.tokens);
  },
  createShareToken() {
    return requestJson<{ ok: boolean; token: CreatedClientToken }>(
      "/auth/tokens",
      jsonInit("POST", { label: "iOS Share Sheet", scopes: ["capture:share"] }),
      (payload) => validClientToken(payload.token, true)
        && payload.token.scopes.length === 1
        && payload.token.scopes[0] === "capture:share",
    ).then((value) => value.token);
  },
  revoke(id: string) {
    return requestJson<{ ok: boolean; token: ClientToken }>(
      `/auth/tokens/${encodeURIComponent(id)}/revoke`,
      jsonInit("POST", {}),
      (payload) => validClientToken(payload.token) && payload.token.id === id && Boolean(payload.token.revoked_at),
    ).then((value) => value.token);
  },
};

export const readerApi = {
  offlineManifest(cursor = "") {
    const query = new URLSearchParams();
    if (cursor) query.set("cursor", cursor);
    const suffix = query.toString();
    return requestJson<OfflineManifestEnvelope>(`/api/reader/offline-manifest${suffix ? `?${suffix}` : ""}`, undefined, validOfflineManifest);
  },
  offlineBundle(items: Array<{ id: string; content_version: string }>) {
    if (!items.length || items.length > MAX_OFFLINE_BUNDLE_ITEMS) return Promise.reject(new RangeError("invalid_offline_bundle_size"));
    const requested = new Map(items.map((item) => [item.id, item.content_version]));
    if (requested.size !== items.length || items.some((item) => !validCanonicalItemId(item.id) || !/^ext_[A-Za-z0-9]{1,96}$/.test(item.content_version))) {
      return Promise.reject(new TypeError("invalid_offline_bundle_items"));
    }
    return requestJson<OfflineBundleEnvelope>(
      "/api/reader/offline-bundle",
      jsonInit("POST", { schema_version: "imprint.offline-bundle.v1", items }),
      (payload) => validOfflineBundle(payload, requested),
    );
  },
  currentBriefing() {
    return requestJson<{ ok: boolean; briefing: Briefing | null }>("/api/reader/abridged/current", undefined, (payload) => validBriefingField(payload, true)).then((value) => value.briefing);
  },
  abridgedDelivery() {
    return requestJson<{ ok: boolean; delivery: AbridgedDeliveryStatus }>(
      "/api/reader/abridged/delivery",
      undefined,
      (payload) => validAbridgedDelivery(payload.delivery),
    ).then((value) => value.delivery);
  },
  briefings(cursor = "") {
    const query = new URLSearchParams({ limit: "25" });
    if (cursor) query.set("cursor", cursor);
    return requestJson<BriefingEnvelope>(`/api/reader/abridged?${query}`, undefined, validBriefingEnvelope);
  },
  briefing(id: string) {
    return requestJson<{ ok: boolean; briefing: Briefing }>(`/api/reader/abridged/${encodeURIComponent(id)}`, undefined, (payload) => validBriefingField(payload, false, id)).then((value) => value.briefing);
  },
  restoreBriefingEntry(briefingId: string, itemId: string) {
    return requestJson<{ ok: boolean; briefing: Briefing }>(
      `/api/reader/abridged/${encodeURIComponent(briefingId)}/entries/${encodeURIComponent(itemId)}/restore`,
      jsonInit("POST", {}),
      (payload) => validBriefingField(payload, false, briefingId),
    ).then((value) => value.briefing);
  },
  listItems(status: string, order: string, cursor = "", favorite?: boolean) {
    const query = new URLSearchParams({ status, order, limit: "50" });
    if (cursor) query.set("cursor", cursor);
    if (favorite !== undefined) query.set("favorite", String(favorite));
    return requestJson<ItemListEnvelope>(`/api/reader/items?${query}`, undefined, (payload) => validItemList(payload, MAX_LIST_ITEMS));
  },
  itemSummaries(ids: string[], status = "queue,inbox") {
    if (ids.length > MAX_ITEM_SUMMARY_IDS) return Promise.reject(new RangeError("too_many_item_ids"));
    if (ids.some((id) => !validCanonicalItemId(id)) || new Set(ids).size !== ids.length) return Promise.reject(new TypeError("invalid_item_ids"));
    const query = new URLSearchParams({ status });
    ids.forEach((id) => query.append("id", id));
    const allowedIds = new Set(ids);
    return requestJson<ItemListEnvelope>(`/api/reader/items/summaries?${query}`, undefined, (payload) => validItemList(payload, ids.length, allowedIds));
  },
  search(queryValue: string, continuation?: { cursor: string; minimumScannedCount: number }, signal?: AbortSignal) {
    if (queryValue.length > MAX_SEARCH_QUERY_CHARS) return Promise.reject(new RangeError("search_query_too_long"));
    const cursor = continuation?.cursor || "";
    const minimumScannedCount = continuation?.minimumScannedCount ?? 0;
    if (continuation && (
      !SEARCH_CURSOR_PATTERN.test(cursor)
      || !validCount(minimumScannedCount)
      || minimumScannedCount > SEARCH_SCAN_LIMIT
    )) return Promise.reject(new TypeError("invalid_search_continuation"));
    const query = new URLSearchParams({ query: queryValue, status: "all", limit: "25" });
    if (cursor) query.set("cursor", cursor);
    return requestJson<SearchItemListEnvelope>(`/api/reader/search?${query}`, { signal }, (payload) => validSearchItemList(
      payload,
      MAX_SEARCH_ITEMS,
      queryValue,
      minimumScannedCount,
    ));
  },
  item(id: string) {
    return requestJson<{ ok: boolean; item: ReaderItem }>(`/api/reader/items/${encodeURIComponent(id)}`, undefined, (payload) => validReaderItemEnvelope(payload, id)).then((value) => value.item);
  },
  save(url: string, title = "", articleText = "") {
    return requestJson<SaveItemEnvelope>("/api/reader/items/save", jsonInit("POST", { url, title, status: "queue", ...(articleText.trim() ? { markdown: articleText.trim() } : {}) }), validSaveItemEnvelope);
  },
  updateState(id: string, state: { status?: string; is_favorite?: boolean; reading_progress?: number }) {
    return requestJson<{ ok: boolean; item: ReaderItem }>(
      `/api/reader/items/${encodeURIComponent(id)}/state`,
      jsonInit("PATCH", state),
      (payload) => validReaderItemEnvelope(payload, id),
    ).then((value) => value.item);
  },
  deleteItem(id: string) {
    if (!validCanonicalItemId(id)) return Promise.reject(new TypeError("invalid_item_id"));
    return requestJson<{ ok: boolean; item_id: string; deleted: boolean; cleanup_pending: boolean }>(
      `/api/reader/items/${encodeURIComponent(id)}?confirm=delete-item`,
      { method: "DELETE" },
      (payload, response) => validDeleteItemResponse(payload, response, id),
    ).catch((error) => {
      if (error instanceof ApiError && error.status === 404 && error.code === "item_not_found") {
        return { ok: true, item_id: id, deleted: true, cleanup_pending: true };
      }
      throw error;
    });
  },
  daily(context: DailyContext) {
    const query = new URLSearchParams({ issue_date: context.issueDate, context_bucket: context.bucket });
    return requestJson<DailyEditionEnvelope>(
      `/api/reader/daily-edition?${query}`,
      undefined,
      (payload) => validDailyForContext(payload, context),
    );
  },
  requestDaily(context: DailyContext) {
    return requestJson<{ ok: boolean; status: string }>(
      "/api/reader/daily-edition/requests",
      jsonInit("POST", { issue_date: context.issueDate, context_bucket: context.bucket }),
      validStatus,
    );
  },
  annotations(itemId: string) {
    return requestJson<AnnotationEnvelope>(`/api/reader/items/${encodeURIComponent(itemId)}/annotations?limit=100`, undefined, validAnnotationEnvelope);
  },
  updateAnnotation(id: string, note: string) {
    return requestJson<{ ok: boolean; annotation: Annotation }>(
      `/api/reader/annotations/${encodeURIComponent(id)}`,
      jsonInit("PATCH", { note }),
      (payload) => validAnnotation(payload.annotation) && payload.annotation.id === id,
    ).then((value) => value.annotation);
  },
  following() {
    return requestJson<FollowingEnvelope>("/api/reader/following?limit=200", undefined, validFollowing);
  },
  setFollowing(id: string, enabled: boolean) {
    return requestJson<{ ok: boolean; feed_source: FeedSource }>(
      `/api/reader/following/${encodeURIComponent(id)}`,
      jsonInit("PATCH", { enabled }),
      (payload) => validFeedSource(payload.feed_source) && payload.feed_source.id === id,
    ).then((value) => value.feed_source);
  },
  readingSession(session: ReadingSessionInput) {
    return requestJson<{ ok: boolean }>("/api/reader/reading-sessions", jsonInit("POST", session), validOk);
  },
  inboxBriefPreferences() {
    return requestJson<{ ok: boolean; preferences: InboxBriefPreferences }>("/api/reader/inbox-brief-preferences", undefined, validInboxPreferences).then((value) => value.preferences);
  },
  updateInboxBriefPreferences(enabled: boolean) {
    return requestJson<{ ok: boolean; preferences: InboxBriefPreferences }>(
      "/api/reader/inbox-brief-preferences",
      jsonInit("PUT", { enabled }),
      validInboxPreferences,
    ).then((value) => value.preferences);
  },
  inboxBriefs(status: "inbox" | "archive") {
    return requestJson<InboxBriefEnvelope>(`/api/reader/inbox-briefs?status=${status}&limit=25`, undefined, validInboxBriefEnvelope);
  },
  inboxBrief(id: string) {
    return requestJson<{ ok: boolean; brief: InboxBrief }>(`/api/reader/inbox-briefs/${encodeURIComponent(id)}?include=markdown`, undefined, (payload) => validInboxBrief(payload.brief) && payload.brief.id === id).then((value) => value.brief);
  },
  requestInboxBrief(issueDate: string) {
    return requestJson<{ ok: boolean; status: string }>(
      "/api/reader/inbox-briefs/requests",
      jsonInit("POST", { issue_date: issueDate }),
      validStatus,
    );
  },
  keepInboxBriefSource(briefId: string, itemId: string) {
    return requestJson<{ ok: boolean; brief: InboxBrief }>(
      `/api/reader/inbox-briefs/${encodeURIComponent(briefId)}/sources/${encodeURIComponent(itemId)}/keep`,
      jsonInit("POST", {}),
      (payload) => validInboxBrief(payload.brief) && payload.brief.id === briefId,
    ).then((value) => value.brief);
  },
  archiveInboxBrief(briefId: string) {
    return requestJson<{ ok: boolean; brief: InboxBrief }>(
      `/api/reader/inbox-briefs/${encodeURIComponent(briefId)}/archive`,
      jsonInit("POST", { confirm: "archive-covered-items" }),
      (payload) => validInboxBrief(payload.brief) && payload.brief.id === briefId,
    ).then((value) => value.brief);
  },
  restoreInboxBriefSource(briefId: string, itemId: string) {
    return requestJson<{ ok: boolean; brief: InboxBrief }>(
      `/api/reader/inbox-briefs/${encodeURIComponent(briefId)}/sources/${encodeURIComponent(itemId)}/restore`,
      jsonInit("POST", {}),
      (payload) => validInboxBrief(payload.brief) && payload.brief.id === briefId,
    ).then((value) => value.brief);
  },
  podcastPreferences() {
    return requestJson<PodcastPreferencesEnvelope>("/api/reader/podcast-preferences", undefined, validPodcastPreferences);
  },
  updatePodcastPreferences(enabled: boolean) {
    return requestJson<PodcastPreferencesEnvelope>(
      "/api/reader/podcast-preferences",
      jsonInit("PUT", { enabled }),
      validPodcastPreferences,
    );
  },
  rotatePodcastFeed() {
    return requestJson<PodcastPreferencesEnvelope>(
      "/api/reader/podcast-feed/rotate",
      jsonInit("POST", {}),
      validPodcastPreferences,
    );
  },
  revokePodcastFeed() {
    return requestJson<PodcastPreferencesEnvelope>(
      "/api/reader/podcast-feed/revoke",
      jsonInit("POST", {}),
      validPodcastPreferences,
    );
  },
  podcastEpisodes(status: "inbox" | "archive") {
    return requestJson<PodcastEpisodeEnvelope>(`/api/reader/podcast-episodes?status=${status}`, undefined, validPodcastEpisodeEnvelope);
  },
  podcastEpisode(id: string) {
    return requestJson<{ ok: boolean; episode: PodcastEpisode }>(`/api/reader/podcast-episodes/${encodeURIComponent(id)}`, undefined, (payload) => validPodcastEpisode(payload.episode) && payload.episode.id === id).then((value) => value.episode);
  },
  backfillPodcastArtwork(episodeId: string) {
    return requestJson<{ ok: boolean; episode_id: string; artwork_jobs_queued: number }>(
      "/api/reader/podcast-artwork/backfill",
      jsonInit("POST", { episode_id: episodeId }),
      (payload) => payload.episode_id === episodeId && hasFiniteNumber(payload, "artwork_jobs_queued") && Number.isInteger(payload.artwork_jobs_queued) && (payload.artwork_jobs_queued as number) >= 0,
    );
  },
  keepPodcastSource(episodeId: string, itemId: string) {
    return requestJson<{ ok: boolean; episode: PodcastEpisode }>(
      `/api/reader/podcast-episodes/${encodeURIComponent(episodeId)}/sources/${encodeURIComponent(itemId)}/keep`,
      jsonInit("POST", {}),
      (payload) => validPodcastEpisode(payload.episode) && payload.episode.id === episodeId,
    ).then((value) => value.episode);
  },
  archivePodcastSources(episodeId: string, itemIds: string[]) {
    return requestJson<{ ok: boolean; episode: PodcastEpisode }>(
      `/api/reader/podcast-episodes/${encodeURIComponent(episodeId)}/archive`,
      jsonInit("POST", { confirm: "archive-selected-sources", item_ids: itemIds }),
      (payload) => validPodcastEpisode(payload.episode) && payload.episode.id === episodeId,
    ).then((value) => value.episode);
  },
  restorePodcastSource(episodeId: string, itemId: string) {
    return requestJson<{ ok: boolean; episode: PodcastEpisode }>(
      `/api/reader/podcast-episodes/${encodeURIComponent(episodeId)}/sources/${encodeURIComponent(itemId)}/restore`,
      jsonInit("POST", {}),
      (payload) => validPodcastEpisode(payload.episode) && payload.episode.id === episodeId,
    ).then((value) => value.episode);
  },
  restoreAllPodcastSources(episodeId: string) {
    return requestJson<{ ok: boolean; episode: PodcastEpisode }>(
      `/api/reader/podcast-episodes/${encodeURIComponent(episodeId)}/restore-all`,
      jsonInit("POST", {}),
      (payload) => validPodcastEpisode(payload.episode) && payload.episode.id === episodeId,
    ).then((value) => value.episode);
  },
};

export function validCanonicalItemId(value: unknown): value is string {
  return typeof value === "string"
    && value.length >= 1
    && value.length <= 180
    && value === value.trim()
    && /^[A-Za-z0-9][A-Za-z0-9:_.-]*$/.test(value);
}

export function validArtworkPath(value: unknown, expectedVariant?: "color" | "eink"): value is string {
  if (typeof value !== "string") return false;
  const match = value.match(/^\/artworks\/([^/]+)\/(color|eink)$/);
  if (!match || (expectedVariant !== undefined && match[2] !== expectedVariant)) return false;
  try {
    const id = decodeURIComponent(match[1]);
    return validCanonicalItemId(id) && encodeURIComponent(id) === match[1];
  } catch {
    return false;
  }
}

function validDeleteItemResponse(payload: JsonObject, response: Response, expectedId: string): boolean {
  const keys = Object.keys(payload).sort();
  return keys.length === 4
    && keys[0] === "cleanup_pending"
    && keys[1] === "deleted"
    && keys[2] === "item_id"
    && keys[3] === "ok"
    && payload.ok === true
    && payload.item_id === expectedId
    && payload.deleted === true
    && typeof payload.cleanup_pending === "boolean"
    && response.status === (payload.cleanup_pending ? 202 : 200);
}
